"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";

import Logo from "./Logo";
import { cn } from "@/lib/utils";

const NAV_LINKS: { label: string; href: string; external?: boolean }[] = [
  { label: "Technology", href: "/technology" },
  { label: "Research", href: "/research" },
  { label: "Blog", href: "/blog" },
  { label: "News", href: "/news" },
  { label: "Careers", href: "/careers" },
  {
    label: "Docs",
    href: "https://qognitive-qcog-core.readthedocs-hosted.com/en/latest/",
    external: true,
  },
];

const MD_MENU = 768;

function linkActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

const pillLinkClass = cn(
  "shrink-0 whitespace-nowrap rounded-lg px-3 py-2 font-heading text-sm font-medium tracking-wide transition-[color,background-color] duration-200 md:px-3.5 md:py-2.5 md:text-[0.9375rem]",
  "text-slate-400 hover:bg-white/[0.06] hover:text-slate-100",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400/40"
);

const pillLinkActiveClass = "bg-white/[0.08] text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]";

const sheetLinkClass = cn(
  "flex min-h-[3rem] w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-left font-heading text-base font-medium text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] transition-colors",
  "active:scale-[0.99] hover:border-white/18 hover:bg-white/[0.1]"
);

const sheetLinkActiveClass = "border-cyan-400/30 bg-cyan-500/[0.12] text-cyan-50 ring-1 ring-cyan-400/20";

export default function Header(): ReactElement {
  const pathname = usePathname() ?? "";
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${MD_MENU}px)`);
    const closeIfDesktop = (): void => {
      if (mq.matches) setMenuOpen(false);
    };
    mq.addEventListener("change", closeIfDesktop);
    return () => mq.removeEventListener("change", closeIfDesktop);
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      document.documentElement.style.overflow = "";
      document.documentElement.style.overscrollBehavior = "";
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
      return;
    }
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      html.style.overflow = "";
      html.style.overscrollBehavior = "";
      body.style.overflow = "";
      body.style.overscrollBehavior = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const closeMenu = (): void => setMenuOpen(false);

  return (
    <header className="pointer-events-none fixed top-0 z-50 flex w-full flex-col items-center">
      {/* Mobile: backdrop + sheet (below md only) */}
      {menuOpen ? (
        <div className="pointer-events-auto md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-[45] bg-black/80 backdrop-blur-md"
            onClick={closeMenu}
          />
          <div
            id="mobile-nav-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            className={cn(
              "fixed left-1/2 z-[48] w-[min(92vw,22rem)] max-h-[min(70vh,32rem)] -translate-x-1/2 overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-[#070b14]/95 p-4 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.75)] backdrop-blur-2xl ring-1 ring-white/[0.06]",
              "top-[max(5.25rem,calc(env(safe-area-inset-top,0px)+4.5rem))]"
            )}
          >
            <p className="mb-3 text-[0.65rem] font-medium uppercase tracking-[0.2em] text-slate-500">Menu</p>
            <nav className="flex flex-col gap-2" aria-label="Mobile primary">
              {NAV_LINKS.map((item) => {
                const active = !item.external && linkActive(pathname, item.href);
                const cls = cn(sheetLinkClass, active && sheetLinkActiveClass);

                if (item.external) {
                  return (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMenu}
                      className={cls}
                    >
                      {item.label}
                    </a>
                  );
                }

                return (
                  <Link key={item.href} href={item.href} onClick={closeMenu} className={cls}>
                    <span>{item.label}</span>
                    <span className="text-sm text-slate-500" aria-hidden>
                      →
                    </span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      ) : null}

      {/* Top pill — stays above overlay */}
      <div
        className={cn(
          "relative z-[55] w-full max-w-[min(72rem,calc(100%-1rem))] px-0 pt-[max(0.65rem,env(safe-area-inset-top,0px))] sm:max-w-[min(80rem,calc(100%-2rem))] md:pt-[max(1rem,env(safe-area-inset-top,0px))]",
          "pointer-events-auto"
        )}
      >
        <div
          className={cn(
            "flex min-h-[3.5rem] flex-row items-center justify-between gap-3 border border-white/10 bg-white/[0.07] px-3 py-2.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.07)] backdrop-blur-xl ring-1 ring-white/[0.06] md:min-h-[3.75rem] md:rounded-2xl md:px-5 md:py-3 md:ring-white/[0.05]",
            "rounded-2xl"
          )}
        >
          <div className="flex min-w-0 shrink-0 items-center">
            <Logo size="md" />
          </div>

          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.08] text-white transition-colors hover:border-white/20 hover:bg-white/[0.12] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400/50 md:hidden"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? "mobile-nav-dialog" : undefined}
            aria-haspopup="dialog"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <X className="h-5 w-5" strokeWidth={1.75} /> : <Menu className="h-5 w-5" strokeWidth={1.75} />}
          </button>

          <nav aria-label="Primary" className="hidden min-w-0 flex-1 flex-nowrap items-center justify-end gap-1.5 md:flex">
            {NAV_LINKS.map((item) => {
              const active = !item.external && linkActive(pathname, item.href);
              const className = cn(pillLinkClass, active && pillLinkActiveClass);

              if (item.external) {
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                  >
                    {item.label}
                  </a>
                );
              }

              return (
                <Link key={item.href} href={item.href} className={className}>
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </header>
  );
}
